import { Form } from "antd"
import { keyBy, orderBy } from "lodash"
import { createContext, useContext, useEffect, useMemo, useState } from "react"

import { projectAtoms } from "#atoms"
import { AddCurriculumFormValues } from "#customTypes/composedTypes/curriculumTypes"
import {
  COMPARATORS,
  DOC_TYPES,
  FirebaseQueryConfig,
} from "#customTypes/composedTypes/firebaseTypes"
import { ProjectConstructor } from "#customTypes/composedTypes/projectTypes"
import { Uuid } from "#customTypes/globalTypes"
import {
  useFilteredDocs,
  useSomeDocs,
  useStableIds,
} from "#hooks/jotai/useSubscription"

interface ProjectOption {
  key: string
  title: string
  description: string
}

interface AddCurriculumFormContextValue {
  projectsById: Record<Uuid, ProjectConstructor>
  projectOptions: ProjectOption[]
  allProjects: ProjectConstructor[]
  projectIds: Uuid[]
  setProjectIds: React.Dispatch<React.SetStateAction<Uuid[]>>
}

const AddCurriculumFormContext =
  createContext<AddCurriculumFormContextValue | null>(null)

export const AddCurriculumFormProvider = ({
  children,
  initialProjectIds,
  projectIdsRef,
}: {
  children: React.ReactNode
  initialProjectIds: Uuid[]
  projectIdsRef: React.MutableRefObject<Uuid[]>
}) => {
  const form = Form.useFormInstance<AddCurriculumFormValues>()
  const semester = Form.useWatch("semester", form)

  // Single source of truth for projectIds
  const [projectIds, setProjectIds] = useState<Uuid[]>(initialProjectIds)

  // One-way sync to form (write-only, never read via useWatch)
  // This ensures form values are correct at submit time
  useEffect(() => {
    // form.setFieldValue("projectIds", projectIds)
    projectIdsRef.current = projectIds
  }, [projectIds, form, projectIdsRef])

  const queryConfig = useMemo<FirebaseQueryConfig>(() => {
    return {
      queries: {
        semester: {
          comparator: COMPARATORS.EQUALS,
          value: semester,
        },
      },
    }
  }, [semester])

  // Load projects filtered by semester (for the dropdown/transfer list)
  const allSemesterProjects = useFilteredDocs<ProjectConstructor>(
    projectAtoms,
    DOC_TYPES.PROJECTS,
    queryConfig
  )

  const stableProjectIds = useStableIds(projectIds)
  // Also load the specific projects that are selected in the curriculum
  // This ensures legacy projects from other semesters still load in the timeline
  const selectedProjects = useSomeDocs<ProjectConstructor>(
    projectAtoms,
    DOC_TYPES.PROJECTS,
    stableProjectIds
  )

  // Merge both sets of projects, deduped by ID
  const allProjects = useMemo(() => {
    const combined = [...allSemesterProjects, ...selectedProjects]
    const uniqueById = keyBy(combined, "id")
    return Object.values(uniqueById)
  }, [allSemesterProjects, selectedProjects])

  const projectsById = useMemo(() => {
    return keyBy(allProjects, "id")
  }, [allProjects])

  const projectOptions = useMemo(() => {
    if (!allProjects.length) return []

    return orderBy(
      allProjects?.map((project) => ({
        key: project.id,
        title: project.name,
        description: `(${project.daysToComplete} days)`,
      })),
      "title"
    )
  }, [allProjects])

  return (
    <AddCurriculumFormContext.Provider
      value={{
        projectsById,
        projectOptions,
        allProjects,
        projectIds,
        setProjectIds,
      }}
    >
      {children}
    </AddCurriculumFormContext.Provider>
  )
}

export const useCurriculumFormContext = () => {
  const context = useContext(AddCurriculumFormContext)
  if (!context) {
    throw new Error(
      "useCurriculumForm must be used within an AddCurriculumForm"
    )
  }
  return context
}
